import {
  Box,
  Button,
  Flex,
  FlexProps,
  Icon,
  Image,
  keyframes,
  Text,
  useMediaQuery
} from '@chakra-ui/react'
import { useAppContext } from 'context/AppProvider'
import { useAuthContext } from 'context/AuthProvider/index'
import { useTransactionsContext } from 'context/TransactionsProvider'
import dayjs from 'dayjs'
import {
  Enum_Userspermissionsuser_Onboardingstatus,
  useMyWalletBalanceLazyQuery
} from 'generated/graphql'
import useWindowMaxSize from 'hooks/useWindowMaxSize'
import { get } from 'lodash'
import { NavItem } from 'navigation'
import { PrivateRouteObject } from 'navigation/routes'
import * as React from 'react'
import { AiTwotoneLock } from 'react-icons/ai'
import { IoMdClose } from 'react-icons/io'
import { IoChevronDown } from 'react-icons/io5'
import { useHistory, useLocation } from 'react-router-dom'
import { images } from 'theme'
import { formatPrice } from 'utils'
import { useNotificationsContext } from '../../context/NotificationsProvider'
import Breadcrumbs from '../Breadcrumbs'
import NotifierBell from '../NotifierBell'

type SideBarProps = FlexProps & {
  accentColor: string
  borderColor?: string
  closeOnNavigate?: boolean
  color: string
  hoverColor: string
  navItems: NavItem[]
  tooltipBg?: string
  tooltipColor?: string
  routes: PrivateRouteObject[]
}

const SideBar: React.FC<SideBarProps> = ({ children, navItems }) => {
  // const { isOpen, onOpen, onClose } = useDisclosure()
  const { user, userRole, logout, walletCurrency } = useAuthContext()
  // const [index, setIndex] = React.useState(0)
  const { notifications } = useNotificationsContext()
  const { walletBalance, setBalance } = useTransactionsContext()
  const { maxWidth, maxHeight } = useWindowMaxSize()
  const { pathname } = useLocation()
  const { push } = useHistory()
  const [isMobileSize] = useMediaQuery('(max-width: 1024px)')
  const { drawerOpen, toggleDrawer, closeDrawer } = useAppContext()

  const [myWalletBalance] = useMyWalletBalanceLazyQuery({
    onCompleted: (res) => {
      setBalance?.(res?.myWalletBalance as number)
    },
    fetchPolicy: 'no-cache'
  })
  React.useEffect(() => {
    myWalletBalance({
      variables: { currency: walletCurrency }
    })
  }, [walletCurrency])

  const history = useHistory()
  // const toast = useToast()
  // const [changeCurrency, { loading }] = useChangeCurrencyMutation({
  //   onCompleted: (res) => {
  //     setUser &&
  //       setUser({ ...user, currency: res?.changeCurrency?.currency } as UsersPermissionsUser)
  //     toast({
  //       ...SUCCESS_TOAST,
  //       description: 'You have successfully switched the currency!'
  //     })
  //   },
  //   onError: (error) => handleErrors(error, toast)
  // })
  const sidebarRef = React.useRef<HTMLDivElement>(null)
  const navListRef = React.useRef<HTMLDivElement>(null)
  const [showScrollHint, setShowScrollHint] = React.useState(false)

  const checkScrollable = React.useCallback(() => {
    const el = navListRef.current
    if (!el) return
    const hasMore = el.scrollHeight - el.scrollTop - el.clientHeight > 10
    setShowScrollHint(hasMore)
  }, [])

  React.useEffect(() => {
    checkScrollable()
    window.addEventListener('resize', checkScrollable)
    return () => window.removeEventListener('resize', checkScrollable)
  }, [checkScrollable, navItems])

  React.useEffect(() => {
    const unlisten = history.listen(() => {
      closeDrawer()
    })

    const handleClickOutside = (event: MouseEvent) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target as Node)) {
        closeDrawer()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)

    return () => {
      unlisten()
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [history, closeDrawer])

  // React.useEffect(() => {
  //   if (!loading) {
  //     onClose()
  //   }
  // }, [loading])

  React.useEffect(() => {
    closeDrawer()
  }, [maxWidth])
  // React.useEffect(() => {
  //   if (walletCurrency === 'KES') {
  //     setIndex(0)
  //   } else {
  //     setIndex(1)
  //   }
  // }, [walletCurrency])
  // const switchCurrency = (currency: string) => {
  //   if (walletCurrency === currency) return
  //   onOpen()
  //   changeCurrency({
  //     variables: { input: { currency } }
  //   })
  // }

  if (
    user?.role?.type === 'corporate' &&
    user?.onboardingStatus !== Enum_Userspermissionsuser_Onboardingstatus.Completed
  ) {
    return (
      <Box width="100vw" height={maxHeight}>
        {children}
      </Box>
    )
  }

  const companyName = get(user, 'company.name')
  const activeSince = get(user, 'company.activeSince')

  const fullName = `${get(user, 'firstName', '')} ${get(user, 'lastName', '')}`

  return (
    <Flex height={maxHeight} bg="background" width="100%" flexDirection="row" position="relative">
      <Flex position="relative" width={isMobileSize ? '0px' : '20%'} zIndex={1000} ref={sidebarRef}>
        <Flex
          position="fixed"
          left={0}
          top={0}
          bottom={0}
          height="100%"
          width={isMobileSize ? 'auto' : '20%'}
        >
          <Flex
            paddingTop={5}
            display={!drawerOpen && isMobileSize ? 'none' : 'flex'}
            borderRadius={isMobileSize ? 0 : 30}
            height={isMobileSize ? maxHeight : '96%'}
            maxWidth="350px"
            minWidth="257px"
            boxShadow="5px 7px 42px 3px rgba(0,0,0,0.2)"
            borderColor="text"
            bg="white"
            m={isMobileSize ? 0 : 'auto 15px'}
            alignItems="center"
            flexDirection="column"
            overflow="hidden auto"
          >
            <Icon
              mr={2}
              display={isMobileSize ? 'block' : 'none'}
              onClick={() => {
                toggleDrawer()
              }}
              position="absolute"
              height="25px"
              w="25px"
              alignSelf="flex-end"
              as={IoMdClose}
            ></Icon>

            <Image src={images.logo} width="6rem" objectFit="contain" />
            <Flex flexDirection="row" alignItems="center" mt={5}>
              <Image
                borderRadius="full"
                src={
                  userRole === 'corporate' ? user?.company?.logo?.url : user?.profilePicture?.url
                }
                fallbackSrc={images.logo}
                width="3rem"
                height="3rem"
                shadow="lg"
                objectFit="cover"
              />
              {userRole === 'corporate' && (
                <Flex flexDirection="column" ml={2}>
                  {companyName && (
                    <Text fontWeight="semibold" fontSize="sm">
                      {companyName}
                    </Text>
                  )}
                  <Text fontSize="xs">{`Active since ${dayjs(activeSince).format('YYYY')}`}</Text>
                </Flex>
              )}
              {userRole === 'investor' && (
                <Flex flexDirection="column" ml={2}>
                  {!!fullName.trim().length && (
                    <Text fontWeight="semibold" fontSize="sm">
                      {fullName}
                    </Text>
                  )}
                  <Text fontSize="xs">{`${
                    walletBalance
                      ? formatPrice(walletBalance, false, walletCurrency)
                      : formatPrice(0, false, walletCurrency)
                  } Available`}</Text>
                </Flex>
              )}
              {userRole === 'regulatory' && (
                <Flex flexDirection="column" ml={2}>
                  {fullName.trim().length && (
                    <Text fontWeight="semibold" fontSize="sm">
                      {fullName}
                    </Text>
                  )}
                  <Text fontSize="xs">Regulatory Body</Text>
                </Flex>
              )}
              {userRole === 'manager' && (
                <Flex flexDirection="column" ml={2}>
                  {fullName.trim().length && (
                    <Text fontWeight="semibold" fontSize="sm">
                      {fullName}
                    </Text>
                  )}
                  <Text fontSize="xs">Administrator</Text>
                </Flex>
              )}
            </Flex>
            {/* {<Flex
              fontSize="12px"
              border="1px solid"
              borderColor="gray.300"
              borderRadius="md"
              mt={5}
              width="75%"
            >
              <Tabs index={index} w="100%" variant="soft-rounded" colorScheme="teal">
                <TabList>
                  <Tab
                    disabled={loading}
                    onClick={() => switchCurrency('KES')}
                    w="50%"
                    py={0}
                    fontSize="sm"
                    _selected={{ color: 'white', bg: 'teal.500', borderRadius: 'md' }}
                  >
                    KES
                  </Tab>
                  <Tab
                    disabled={loading}
                    onClick={() => switchCurrency('USD')}
                    w="50%"
                    py={0}
                    fontSize="sm"
                    _selected={{ color: 'white', bg: 'teal.500', borderRadius: 'md' }}
                  >
                    USD
                  </Tab>
                </TabList>
              </Tabs>
            </Flex>} */}

            <Flex
              mt={5}
              width="100%"
              flexDirection="column"
              pr={5}
              flex={1}
              overflow="hidden auto"
              position="relative"
              ref={navListRef}
              onScroll={checkScrollable}
            >
              {navItems.length &&
                navItems.map((navItem, index) => {
                  const { icon, title, to } = navItem
                  const isActive = pathname.includes(to)

                  return (
                    <Flex
                      position="relative"
                      key={index}
                      borderLeftWidth={8}
                      borderLeftColor={isActive ? 'secondary' : 'transparent'}
                      bg={isActive ? 'transparentSecondary' : 'transparent'}
                      width="100%"
                      borderTopRightRadius={10}
                      borderBottomRightRadius={10}
                      alignItems="center"
                      color={isActive ? 'secondary' : 'text'}
                      my={1}
                      _hover={{
                        cursor: 'pointer',
                        borderLeftColor: 'secondary',
                        bg: 'transparentSecondary',
                        color: 'secondary'
                      }}
                      onClick={() => push(to)}
                    >
                      <Box mx={3}>{icon}</Box>
                      <Text my={3} fontWeight={isActive ? 'bold' : 'normal'}>
                        {title}
                      </Text>
                      {to === '/auth/notifications' && (
                        <NotifierBell>
                          <Text fontSize={12} fontWeight={400} color="white" textAlign="center">
                            {notifications ? notifications.length : 0}
                          </Text>
                        </NotifierBell>
                      )}
                    </Flex>
                  )
                })}
              <Button
                variant="link"
                my={5}
                color="secondary"
                display={isMobileSize ? 'flex' : 'none'}
                leftIcon={<AiTwotoneLock size={30} />}
                onClick={() => {
                  logout && logout()
                  push('/')
                }}
                _hover={{
                  color: 'transparentSecondary'
                }}
              >
                Log Out
              </Button>
            </Flex>
            {showScrollHint && (
              <Flex
                width="100%"
                justifyContent="center"
                py={1}
                cursor="pointer"
                onClick={() => {
                  navListRef.current?.scrollBy({ top: 80, behavior: 'smooth' })
                }}
              >
                <Icon
                  as={IoChevronDown}
                  color="secondary"
                  boxSize={5}
                  animation={`${keyframes`
                    0%, 100% { transform: translateY(0); opacity: 0.6; }
                    50% { transform: translateY(6px); opacity: 1; }
                  `} 1.5s ease-in-out infinite`}
                />
              </Flex>
            )}
            <Flex
              display={isMobileSize ? 'none' : 'flex'}
              marginTop="auto"
              bgImage={`url(${images.waveIcon})`}
              bgRepeat="no-repeat"
              bgPosition={isMobileSize ? '80% 65%' : 'center'}
              bgSize="auto"
              minHeight={isMobileSize ? '7rem' : '13rem'}
              width="100%"
              alignItems="center"
              direction="column"
              justifyContent="flex-end"
              paddingBottom={10}
            >
              <Button
                variant="link"
                color="white"
                leftIcon={<AiTwotoneLock size={30} />}
                onClick={() => {
                  logout && logout()
                  push('/')
                }}
                _hover={{
                  color: 'secondary'
                }}
              >
                Log Out
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Flex>
      <Flex flexDirection="column" flex={1} width={isMobileSize ? '100%' : '75%'}>
        <Flex height={{ base: maxHeight }} boxSizing="border-box" overflow="hidden">
          <Flex
            width="100%"
            boxSizing="border-box"
            direction="column"
            position="relative"
            overflowY="scroll"
          >
            <Breadcrumbs />
            {children}
          </Flex>
        </Flex>
      </Flex>
      {/* <Modal isOpen={isOpen} onClose={onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalBody alignSelf="center" w="100%">
            <Flex justifyContent="center" alignItems="center" w="100%">
              <Flex justifyContent="space-around" w="100%" alignItems="center">
                <Image h={20} src={images.kesBill} />
                <PulseLoader
                  color="#4B69B2"
                  margin={6}
                  style={{
                    transform: walletCurrency === 'KES' ? 'rotate(360deg)' : 'rotate(180deg)'
                  }}
                />
                <Image h={20} src={images.usdBill} />
              </Flex>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal> */}
    </Flex>
  )
}

export default SideBar

SideBar.defaultProps = {
  color: 'white',
  bg: 'gray.900',
  hoverColor: 'gray.800',
  borderColor: 'gray.700',
  accentColor: 'cyan.500'
}
